// LocationsTable.js (Modified)
import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { CSVLink } from 'react-csv';
import { Icon } from '@iconify/react';
import { Link, useLocation } from 'react-router-dom'; // Import useLocation
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';

import api from '../../services/api';
import ViewLocationModal from './ViewLocationModal';
import EditLocationModal from './EditLocationModal';
import DeleteConfirmationModal from '../DeleteConfirmationModal';

const LocationsTable = () => {
    const [data, setData] = useState([]);
    const [totalItems, setTotalItems] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10); // Initial perPage value
    const [searchQuery, setSearchQuery] = useState('');
    const [sortColumn, setSortColumn] = useState('name');
    const [sortDirection, setSortDirection] = useState('asc');

    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);

    const [currentLocation, setCurrentLocation] = useState(null);
    const [editedLocation, setEditedLocation] = useState(null);
    const [deletedLocation, setDeletedLocation] = useState(null);

    const location = useLocation(); // Get the location object

    // **IMPORTANT:**  This function now handles ALL parameters
    const fetchLocations = async (page, perPage, search, sort, direction) => {
        try {
            const response = await api.get(`/location?page=${page}&per_page=${perPage}&search=${search}&sort=${sort}&direction=${direction}`);
            setData(response.data.response.data);
            setTotalItems(response.data.response.total);
        } catch (error) {
            console.error('Error fetching locations:', error);
            // Consider adding user-friendly error handling (e.g., a toast notification)
        }
    };
    //initial fetch
    useEffect(() => {
        fetchLocations(currentPage, perPage, searchQuery, sortColumn, sortDirection);
    }, []);

    //updates when pagenation changes
    useEffect(() => {
        fetchLocations(currentPage, perPage, searchQuery, sortColumn, sortDirection);
    }, [currentPage, perPage, searchQuery, sortColumn, sortDirection]);

    // Add this useEffect to listen for the refresh signal
    useEffect(() => {
        if (location.state?.refresh) {
            fetchLocations(currentPage, perPage, searchQuery, sortColumn, sortDirection);
            // Clear the state to prevent constant refreshing
            location.state.refresh = false; // Or use history.replace to modify the state
        }
    }, [location.state, currentPage, perPage, searchQuery, sortColumn, sortDirection]); // Include dependencies


    const handlePageChange = (page) => {
        setCurrentPage(page);
        //No need to call fetchLocations here the use effect will do it.
    };

    const handlePerRowsChange = async (newPerPage, page) => {
        setPerPage(newPerPage);
        setCurrentPage(1); // Reset to page 1 when changing rows per page.  VERY IMPORTANT!
        //No need to call fetchLocations here the use effect will do it.
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1); // Reset to page 1 on search. IMPORTANT!
        //No need to call fetchLocations here the use effect will do it.
    };

    const handleSort = (column, direction) => {
        setSortColumn(column.selector);
        setSortDirection(direction);
        //No need to call fetchLocations here the use effect will do it.
    };

    const handleView = (location) => {
        setCurrentLocation(location);
        setShowViewModal(true);
    };

    const handleCloseViewModal = () => {
        setShowViewModal(false);
        setCurrentLocation(null);
        //fetchLocations(currentPage, perPage, searchQuery, sortColumn, sortDirection); //Refetch to get updated data.
    };

    const handleEdit = (location) => {
        setEditedLocation(location);
        setShowEditModal(true);
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setEditedLocation(null);
        //fetchLocations(currentPage, perPage, searchQuery, sortColumn, sortDirection); //Refetch is good practice.
    };

    const handleSaveEdit = (updatedLocation) => {
        //No need to call fetchLocations here since the edit modal handles it
        setShowEditModal(false);
        setEditedLocation(null);
    };

    const handleDeleteLocation = (location) => {
        setDeletedLocation(location);
        setShowDeleteConfirmationModal(true);
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteConfirmationModal(false);
        setDeletedLocation(null);
        //fetchLocations(currentPage, perPage, searchQuery, sortColumn, sortDirection); //Refetch on closing delete confirm.
    };

    const handleDelete = async () => {
        try {
            await api.delete(`/location/${deletedLocation.id}`);
            toast.success('Location deleted successfully!');
            //fetchLocations(currentPage, perPage, searchQuery, sortColumn, sortDirection); //Refetch after successful delete.  GOOD!
        } catch (error) {
            toast.error('Error deleting location.');
            console.error(error);
        } finally {
            handleCloseDeleteModal(); // Close the modal in *all* cases (success or error).
        }
    };

    const columns = [
        {
            name: 'Name',
            selector: (row) => row.name,
            sortable: true,
        },
        {
            name: 'Actions',
            cell: (row) => (
                <div className="d-flex">
                    <button
                        onClick={() => handleView(row)}
                        className="w-32-px h-32-px me-8 bg-primary-light text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center"
                    >
                        <Icon icon="iconamoon:eye-light" />
                    </button>
                    <button
                        onClick={() => handleEdit(row)}
                        className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                    >
                        <Icon icon="lucide:edit" />
                    </button>
                    <button
                        onClick={() => handleDeleteLocation(row)}
                        className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                    >
                        <Icon icon="mingcute:delete-2-line" />
                    </button>
                </div>
            ),
            width: '1000px',
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        },
    ];

    const csvHeaders = [
        { label: 'Location ID', key: 'id' },
        { label: 'Name', key: 'name' },
    ];

    const customStyles = {
        rows: {
            style: {
                fontSize: '15px',
            },
        },
    };


    return (
        <div className="card basic-data-table px-3 py-3">
            <div className="card-body">
                <div className="d-flex justify-content-between mb-2">
                    <CSVLink
                        data={data} // Use data array for CSV export
                        headers={csvHeaders}
                        filename="locations.csv"
                        className="btn btn-secondary"
                    >
                        Export CSV
                    </CSVLink>
                    <Link to="/add-location" className="btn btn-primary btn-sm d-flex align-items-center gap-2">
                        <Icon icon="ic:baseline-plus" className="icon text-sm" />
                        Add New Location
                    </Link>
                </div>
                <input
                    type="text"
                    placeholder="Search locations..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="form-control mb-3"
                />

                <DataTable
                    columns={columns}
                    data={data} // Use data now
                    pagination
                    paginationServer
                    paginationTotalRows={totalItems}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                    onSort={handleSort}
                    sortServer
                    highlightOnHover
                    responsive
                    striped
                    customStyles={customStyles}
                    noDataComponent="No results"
                />
            </div>

            {showViewModal && (
                <ViewLocationModal
                    show={showViewModal}
                    handleClose={handleCloseViewModal}
                    location={currentLocation}
                />
            )}

            {showEditModal && (
                <EditLocationModal
                    show={showEditModal}
                    handleClose={handleCloseEditModal}
                    location={editedLocation}
                    handleSave={handleSaveEdit}
                />
            )}

            <DeleteConfirmationModal
                show={showDeleteConfirmationModal}
                handleClose={handleCloseDeleteModal}
                handleDelete={handleDelete}
                content={
                    <>
                        <Modal.Header closeButton>
                            <Modal.Title>Delete Location</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <p>Are you sure you want to delete this Location?</p>
                        </Modal.Body>
                    </>
                }
            />
        </div>
    );
};

export default LocationsTable;